/**
 * Secure storage utilities
 * 
 * Uses localStorage for persistent data (remember me functionality)
 * localStorage is appropriate here because:
 * - We need persistence across browser sessions for "Remember Me"
 * - Sensitive data (tokens) should be short-lived regardless of storage
 * - We implement client-side expiration
 * - localStorage has better XSS protection than cookies
 */

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  USER: 'user',
  LOGIN_TIME: 'loginTime',
  REMEMBER_ME: 'rememberMe',
  PENDING_VERIFICATION_EMAIL: 'pendingVerificationEmail',
  SELECTED_CLIENT_ID: 'selectedClientId',
} as const;

/**
 * Set an item in localStorage
 */
export const setItem = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.error(`Failed to set localStorage item ${key}:`, error);
  }
};

/**
 * Get an item from localStorage
 */
export const getItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error(`Failed to get localStorage item ${key}:`, error);
    return null;
  }
};

/**
 * Remove an item from localStorage
 */
export const removeItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Failed to remove localStorage item ${key}:`, error);
  }
};

/**
 * Clear all authentication-related items
 */
export const clearAuth = (): void => {
  Object.values(STORAGE_KEYS).forEach(removeItem);
};

/**
 * Check if remember me is enabled
 */
export const isRememberMeEnabled = (): boolean => {
  return getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
};

/**
 * Get the stored access token
 */
export const getAccessToken = (): string | null => {
  return getItem(STORAGE_KEYS.ACCESS_TOKEN);
};

/**
 * Get the stored user data
 */
export const getUser = (): any => {
  const userData = getItem(STORAGE_KEYS.USER);
  return userData ? JSON.parse(userData) : null;
};

/**
 * Get login time
 */
export const getLoginTime = (): number | null => {
  const loginTime = getItem(STORAGE_KEYS.LOGIN_TIME);
  return loginTime ? parseInt(loginTime) : null;
};

export { STORAGE_KEYS };
